import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  Button,
  IconButton,
  TableContainer,
  Paper,
  Table as TableMaterialUI,
  TableHead,
  TableBody,
  TablePagination,
} from '@material-ui/core';
import { FilterList } from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { isEmpty, isEqual, forEach, isUndefined, includes, find } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import { Progress } from '~/components';
import { canURL, t, filterSerialize, number } from '~/helpers';
import { openDialog, closeDialog } from '~/store/modules/dialog/actions';
import { resetParams } from '~/store/modules/hooks/actions';

import { Thead, Tbody, Mobile, Form, Filter } from './components';
import * as S from './style';

export default function Table({
  state,
  topBar,
  noCreate,
  customFilter,
  header,
  recordFilter,
  noPaginate,
  unique,
  remove,
  findItem,
  noEdition,
  noDelete,
  buttons,
  inputCell,
  headerMobile,
  form,
  modal,
  read,
  noReset,
  onCallback,
  size,
}) {
  const [searchFields, setSearchFields] = useState([]);
  const [tempParameters, setTempParameters] = useState({});

  const [create, setCreate] = useState(true);
  const [checked, setChecked] = useState({});
  const [drawer, setDrawer] = useState(false);
  const [perPage, setPerPage] = useState(state.pageSize);
  const [modCustomFilters, setModCustomFilters] = useState([]);
  const [search, setSearch] = useState({});

  const permission = canURL();
  const dispatch = useDispatch();

  const handleOpenFormCreate = useCallback(() => {
    dispatch(openDialog());
    setCreate(true);
    setChecked({});
  }, [dispatch, openDialog]);

  const handleCloseFormCreate = useCallback(() => {
    dispatch(closeDialog());
    setCreate(true);
  }, [dispatch, closeDialog]);

  const handleToggleDrawer = useCallback((value) => {
    setDrawer(value || false);
  }, []);

  const handleOrderingOfValues = useCallback(
    async (orderfield, ordination, wheres) => {
      setTempParameters(wheres);
      await dispatch(
        recordFilter({
          page: 1,
          pagesize: perPage,
          orderfield,
          ordination,
          wherefield: wheres.wherefield,
          valuewherefield: wheres.valuewherefield,
          typewherefield: wheres.typewherefield,
        })
      );
    },
    [perPage]
  );

  const handleRouteParameters = useCallback(
    async (wheres) => {
      setTempParameters(wheres);
      await dispatch(
        recordFilter({
          page: 1,
          pagesize: state.params.pagesize || perPage,
          ...(state.params.orderfield
            ? {
                orderfield: state.params.orderfield,
                ordination: state.params.ordination,
              }
            : {}),
          wherefield: wheres.wherefield,
          valuewherefield: wheres.valuewherefield,
          typewherefield: wheres.typewherefield,
        })
      );
    },
    [perPage, state.params]
  );

  const handleChangePagination = useCallback(
    async (e, newPage) => {
      const page = Number(newPage + 1);

      await dispatch(
        recordFilter({
          ...(!isEmpty(state.params)
            ? state.params
            : {
                wherefield: [],
                valuewherefield: [],
                typewherefield: [],
              }),
          page,
        })
      );
    },
    [state.params, dispatch, recordFilter]
  );

  const handleChangeRowsPerPage = useCallback(
    async (e) => {
      const pagesize = e.target.value;
      const { params } = state;

      setPerPage(pagesize);

      await dispatch(
        recordFilter({
          ...(!isEmpty(params)
            ? params
            : {
                page: 1,
                wherefield: [],
                valuewherefield: [],
                typewherefield: [],
              }),
          pagesize,
        })
      );
    },
    [state.params, dispatch, recordFilter]
  );

  const handleConfirmRowIsChecked = useCallback(
    (value) => isEqual(value, checked),
    [checked]
  );

  const handleCheckRow = useCallback((value) => {
    console.log(value);
    setChecked(value);
  }, []);

  const handleFetchLineItem = useCallback(
    async (data) => {
      const url = [];

      setCreate(false);
      forEach(unique, (key) => url.push(data[key]));

      await dispatch(findItem(url.join('/')));
      dispatch(openDialog());
    },
    [unique, dispatch]
  );

  const handleChangePaginationMobile = useCallback(
    async (e, page) => {
      const { params } = state;

      await dispatch(
        recordFilter({
          ...params,
          page,
        })
      );
    },
    [state.params, dispatch]
  );

  const handleSubmitFormFielters = useCallback(
    async (data) => {
      setSearch(data.fields);
      await handleRouteParameters(filterSerialize(data.data));
    },
    [filterSerialize, state]
  );

  const handleResetRequest = useCallback(() => {
    if (!noReset) {
      dispatch(resetParams());
    }
  }, [dispatch, resetParams, noReset]);

  const handleRequestData = useCallback(() => {
    dispatch(read());
  }, [dispatch, read]);

  const renderButtonCreate = useMemo(() => {
    if (permission.create && !noCreate) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenFormCreate}
        >
          {t('s.geral.botaoincluir')}
        </Button>
      );
    }

    return null;
  }, [noCreate, permission.create]);

  const renderTableBody = useMemo(
    () =>
      state.data.map((tbody, index) => (
        <Tbody
          key={index}
          columns={header}
          data={tbody}
          selected={handleConfirmRowIsChecked(tbody)}
          addedItemHandler={handleCheckRow}
          unique={unique}
          remove={
            !noDelete && permission.delete
              ? (value) => remove(value, state.params)
              : false
          }
          edit={() => handleFetchLineItem(tbody)}
          edition={permission.update && !noEdition}
          model={isEqual(permission, { read: true })}
          buttons={buttons}
          cell={inputCell}
          onCallback={onCallback}
          size={size}
        />
      )),
    [
      state,
      header,
      checked,
      unique,
      permission,
      noEdition,
      buttons,
      inputCell,
      remove,
      onCallback,
    ]
  );

  const isMobile = useMemo(
    () =>
      window.innerWidth <= 425
        ? header.concat(modCustomFilters)
        : modCustomFilters,
    [window.innerWidth, header, modCustomFilters]
  );

  const headersMobile = useMemo(
    () => header.filter((data) => data.id !== headerMobile),
    [header, headerMobile]
  );

  const renderBodyMobile = useMemo(
    () =>
      state.data.map((tbody, key) => (
        <Mobile
          key={`mobile-${key}`}
          header={tbody[headerMobile]}
          labels={headersMobile}
          content={tbody}
          edit={() => handleFetchLineItem(tbody)}
          unique={unique}
          remove={!noDelete && permission.delete ? remove : false}
          edition={permission.update && !noEdition}
          onCallback={onCallback}
        />
      )),
    [
      state.data,
      headerMobile,
      headersMobile,
      unique,
      permission.delete,
      remove,
      permission.update,
      noEdition,
    ]
  );

  useEffect(() => {
    const tempArray = header.map((field) => {
      let filterValue = '';
      let filterType = '';

      if (!isEmpty(tempParameters)) {
        tempParameters.wherefield.forEach((item, index) => {
          if (item == field.id) {
            filterValue = tempParameters.valuewherefield[index];
            filterType = tempParameters.typeField[index];
          }
        });
      }
      return {
        id: field.id,
        value: filterValue,
        type: filterType,
      };
    });
    setSearchFields(tempArray);
  }, [header, tempParameters]);

  useEffect(() => {
    const where = find(
      header,
      (data) =>
        !isUndefined(data.filter) && !isUndefined(data.filter.defaultValue)
    );

    if (isUndefined(where)) {
      handleResetRequest();
      // handleRequestData();
    }
  }, []);

  useEffect(() => {
    const where = customFilter.map(({ id, label, ...rest }) => ({
      id,
      label,
      filter: {
        ...rest,
      },
    }));

    if (!isEmpty(customFilter)) {
      setModCustomFilters(where);
    }
  }, [customFilter]);

  return (
    <>
      <S.Container>
        <Progress loading={state.loading} />
        <S.Created>
          {topBar && topBar()}
          {renderButtonCreate}

          <IconButton
            color="primary"
            onClick={() => handleToggleDrawer(true)}
            className={isEmpty(customFilter) ? 'mobile' : ''}
          >
            <FilterList />
          </IconButton>
        </S.Created>

        <TableContainer component={Paper} className="desktop">
          <TableMaterialUI aria-label="simple table" size={size}>
            <TableHead>
              <Thead
                headers={header}
                orderBy={state.params.orderfield}
                order={state.params.ordination}
                createSortHandler={handleOrderingOfValues}
                filters={handleRouteParameters}
                fields={searchFields}
              />
            </TableHead>
            <TableBody>{renderTableBody}</TableBody>
          </TableMaterialUI>
          {!noPaginate && (
            <TablePagination
              component="div"
              rowsPerPageOptions={size && [10]}
              count={state.totalCount}
              page={Number(state.page - 1)}
              onChangePage={handleChangePagination}
              rowsPerPage={state.pageSize}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              labelRowsPerPage={t('s.geral.linhasporpagina')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${number(to, 0)} ${t('s.geral.de')} ${number(
                  count,
                  0
                )}`
              }
            />
          )}
        </TableContainer>

        <div className="mobile">
          {renderBodyMobile}
          <Pagination
            count={state.totalPages}
            page={state.page}
            onChange={handleChangePaginationMobile}
          />
        </div>

        {!!form && (
          <Form
            close={handleCloseFormCreate}
            component={form}
            create={create}
            loading={state.loading}
            active={checked}
            size={modal}
          />
        )}
      </S.Container>

      <Filter
        open={drawer}
        close={handleToggleDrawer}
        fields={isMobile}
        onSubmit={handleSubmitFormFielters}
        search={search}
        initials={isMobile
          .filter((head) => !isUndefined(head.filter))
          .map((head) => {
            if (
              includes(['initialdatetime', 'finaldatetime'], head.filter.type)
            ) {
              return {
                ...head,
                filter: {
                  ...head.filter,
                  defaultValue: moment(head.filter.defaultValue).format(
                    'DD/MM/YYYY HH:mm:ss'
                  ),
                },
              };
            }

            return head;
          })
          .reduce((accumulator, currentValue) => {
            accumulator[currentValue.id] =
              currentValue.filter.defaultValue || '';

            return accumulator;
          }, {})}
      />
    </>
  );
}

Table.propTypes = {
  header: PropTypes.arrayOf(PropTypes.object).isRequired,
  headerMobile: PropTypes.string.isRequired,
  unique: PropTypes.arrayOf(PropTypes.string).isRequired,
  state: PropTypes.objectOf(PropTypes.any).isRequired,
  read: PropTypes.func.isRequired,
  remove: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  form: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.bool,
  ]),
  findItem: PropTypes.func,
  recordFilter: PropTypes.func.isRequired,
  noCreate: PropTypes.bool,
  noEdition: PropTypes.bool,
  noDelete: PropTypes.bool,
  buttons: PropTypes.arrayOf(PropTypes.any),
  topBar: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.bool,
  ]),
  customFilter: PropTypes.arrayOf(PropTypes.any),
  inputCell: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  noPaginate: PropTypes.bool,
  modal: PropTypes.oneOf(['lg', 'md', 'sm', 'xl', 'xs']),
  noReset: PropTypes.bool,
  onCallback: PropTypes.func,
  size: PropTypes.string,
};

Table.defaultProps = {
  remove: false,
  form: false,
  findItem: () => {},
  noCreate: false,
  noEdition: false,
  noDelete: false,
  buttons: [],
  topBar: false,
  customFilter: [],
  inputCell: false,
  noPaginate: false,
  modal: 'sm',
  noReset: false,
  onCallback: () => undefined,
  size: 'default',
};
